import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react'
import * as Sentry from '@sentry/react'
import { api, setAuthToken, registerTokenResolver, e2eToken } from '../lib/api'
import { getEntryContext, setEntryContext } from '../lib/entryContext'
import { clearLegacyAuthStorage } from '../lib/legacyStorage'
import {
  type Persona,
  heldPersonas,
  resolveActivePersona,
  navPermissionChecker,
} from '../lib/personas'

export { clearEntryContext } from '../lib/entryContext'
export type { Persona } from '../lib/personas'

/** Legacy API role for backward compatibility with older UI checks. */
export type Role = 'OWNER' | 'WORKER' | 'MANAGER' | 'NONE'

export type CompanyRole = 'merchant' | 'manager' | 'worker' | null

export type CompanyApprovalStatus = 'none' | 'pending' | 'approved' | 'rejected'

export interface MePayload {
  companyId?: string | null
  companyName?: string | null
  role?: Role
  owner?: boolean
  companyRole?: CompanyRole
  companyApprovalStatus?: string | null
  permissions?: string[]
  isPlatformAdmin?: boolean
  storeScope?: string[]
  allStores?: boolean
  impersonation?: { targetUserId: string; companyId?: string | null; targetName?: string | null } | null
  /** The active company's package; null without a company (and absent on an older core). */
  package?: { id: string; name: string } | null
  /** Module keys that are on for the active company; null or absent means every module is on. */
  modules?: string[] | null
  /** The package's offering visibility policy: everyone | limited_only | everyone_after_approval. */
  offeringVisibility?: string | null
}

interface CourierMePayload {
  userId?: string
  permissions?: string[]
  online?: boolean
}

interface PartnerCompanyInfo {
  id?: string
  code?: string
  displayName?: string
  /** The number shops call about a job; set on the desk's coverage page. */
  dispatchPhone?: string | null
}

interface PartnerMePayload {
  userId?: string
  permissions?: string[]
  company?: PartnerCompanyInfo | null
}

export interface AuthState {
  role: Role
  companyRole: CompanyRole
  companyId: string | null
  companyName: string | null
  companyApprovalStatus: CompanyApprovalStatus
  isOwner: boolean
  isPlatformAdmin: boolean
  isCourier: boolean
  isCourierPartner: boolean
  partnerCompany: PartnerCompanyInfo | null
  permissions: string[]
  courierPermissions: string[]
  partnerPermissions: string[]
  storeScope: string[]
  allStores: boolean
  impersonation: MePayload['impersonation']
  /** The active company's package, or null. */
  companyPackage: { id: string; name: string } | null
  /**
   * Module keys that are on for the active company. null — an older core, or no
   * company — means every module is on; read it with moduleEnabled() (lib/packages).
   */
  modules: string[] | null
  /** The package's offering visibility policy, or null when /api/me doesn't say. */
  offeringVisibility: string | null
  /** True until /api/me has resolved (or failed) after authentication. */
  loading: boolean
  /**
   * Why the account (GET /api/me) couldn't be loaded, or null. A failed load is not
   * "no company": the app shows an error with Retry, never the apply form.
   */
  accountLoadError: string | null
  /**
   * Approved company member with no grants — not admin, not courier, not partner.
   * New / pending / rejected applicants are not workerOnly so they can apply.
   */
  workerOnly: boolean
  /** Personas the user actually holds. */
  availablePersonas: Persona[]
  /** Active persona for nav (preview or resolved from membership). */
  activePersona: Persona | 'None'
  hasPermission: (code: string) => boolean
  /** Sidebar visibility — may apply persona preview bundles. */
  hasNavPermission: (code: string) => boolean
  switchCompany: (companyId: string) => Promise<void>
  refreshMe: () => Promise<void>
  /** Loads the account again after a failure (the Retry button). */
  retryAccountLoad: () => void
  startImpersonation: (userId: string, companyId: string) => Promise<void>
  endImpersonation: () => Promise<void>
  activeRole: string
  setPreviewRole: (role: string | null) => void
}

const EMPTY_PERMISSIONS: string[] = []

const AUTH_BOOTSTRAP_TIMEOUT_MS = 25_000

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    }),
  ])
}

const DEFAULT: AuthState = {
  role: 'NONE',
  companyRole: null,
  companyId: null,
  companyName: null,
  companyApprovalStatus: 'none',
  isOwner: false,
  isPlatformAdmin: false,
  isCourier: false,
  isCourierPartner: false,
  partnerCompany: null,
  permissions: EMPTY_PERMISSIONS,
  courierPermissions: EMPTY_PERMISSIONS,
  partnerPermissions: EMPTY_PERMISSIONS,
  storeScope: [],
  allStores: false,
  impersonation: null,
  companyPackage: null,
  modules: null,
  offeringVisibility: null,
  loading: true,
  accountLoadError: null,
  workerOnly: false,
  availablePersonas: [],
  activePersona: 'None',
  hasPermission: () => false,
  hasNavPermission: () => false,
  switchCompany: async () => {},
  refreshMe: async () => {},
  retryAccountLoad: () => {},
  startImpersonation: async () => {},
  endImpersonation: async () => {},
  activeRole: 'None',
  setPreviewRole: () => {},
}

const AuthContext = createContext<AuthState>(DEFAULT)

type AuthStateCore = Omit<
  AuthState,
  | 'switchCompany'
  | 'setPreviewRole'
  | 'hasPermission'
  | 'hasNavPermission'
  | 'refreshMe'
  | 'retryAccountLoad'
  | 'startImpersonation'
  | 'endImpersonation'
  | 'availablePersonas'
  | 'activePersona'
>

/** The account. Nothing is written to disk: the token and the permission list stay in memory. */
async function fetchMe(): Promise<MePayload> {
  const { data } = await api.get<MePayload>('/me')
  return data
}

async function fetchCourierMe(): Promise<{ isCourier: boolean; courierPermissions: string[] }> {
  try {
    const { data } = await api.get<CourierMePayload>('/courier/me')
    const courierPermissions = Array.isArray(data?.permissions) ? [...data.permissions] : []
    return { isCourier: true, courierPermissions }
  } catch {
    return { isCourier: false, courierPermissions: [] }
  }
}

async function fetchPartnerMe(): Promise<{
  isCourierPartner: boolean
  partnerPermissions: string[]
  partnerCompany: PartnerCompanyInfo | null
}> {
  try {
    const { data } = await api.get<PartnerMePayload>('/partner/me')
    const partnerPermissions = Array.isArray(data?.permissions) ? [...data.permissions] : []
    const partnerCompany = data?.company && typeof data.company === 'object' ? data.company : null
    return { isCourierPartner: true, partnerPermissions, partnerCompany }
  } catch {
    return { isCourierPartner: false, partnerPermissions: [], partnerCompany: null }
  }
}

function roleLabel(
  role: Role,
  companyRole: CompanyRole,
  isCourier: boolean,
  isCourierPartner = false,
  partnerCompanyName?: string | null,
): string {
  if (companyRole === 'merchant' || role === 'OWNER') return 'Merchant'
  if (companyRole === 'manager' || role === 'MANAGER') return 'Manager'
  if (companyRole === 'worker' || role === 'WORKER') return 'Worker'
  if (isCourier) return 'Courier'
  if (isCourierPartner) return partnerCompanyName?.trim() || 'Partner'
  return 'None'
}

function normalizeCompanyApprovalStatus(
  status: string | null | undefined,
  companyId: string | null,
): CompanyApprovalStatus {
  if (!status || status === 'none') {
    return companyId ? 'pending' : 'none'
  }
  if (status === 'pending' || status === 'approved' || status === 'rejected') {
    return status
  }
  return companyId ? 'approved' : 'none'
}

export function applyMePayload(
  data: MePayload | null | undefined,
  courier: { isCourier: boolean; courierPermissions: string[] } = {
    isCourier: false,
    courierPermissions: [],
  },
  partner: {
    isCourierPartner: boolean
    partnerPermissions: string[]
    partnerCompany?: PartnerCompanyInfo | null
  } = {
    isCourierPartner: false,
    partnerPermissions: [],
    partnerCompany: null,
  },
) {
  const role: Role = data?.role ?? 'NONE'
  const companyRole: CompanyRole = data?.companyRole ?? null
  const companyId = data?.companyId ?? null
  const companyPermissions = Array.isArray(data?.permissions) ? [...data.permissions] : []
  const isPlatformAdmin = Boolean(data?.isPlatformAdmin)
  const isCourier = courier.isCourier
  const courierPermissions = courier.courierPermissions
  const isCourierPartner = partner.isCourierPartner
  const partnerPermissions = partner.partnerPermissions
  const partnerCompany = partner.partnerCompany ?? null
  const companyApprovalStatus = normalizeCompanyApprovalStatus(data?.companyApprovalStatus, companyId)

  // Deny only approved members with no grants. Fresh signups (approval none)
  // and pending/rejected applicants must reach /onboarding/*.
  const workerOnly =
    !isPlatformAdmin &&
    !isCourier &&
    !isCourierPartner &&
    companyPermissions.length === 0 &&
    companyApprovalStatus === 'approved'

  return {
    role,
    companyRole,
    companyId,
    companyName: data?.companyName ?? null,
    companyApprovalStatus,
    isOwner: role === 'OWNER' || Boolean(data?.owner),
    isPlatformAdmin,
    isCourier,
    isCourierPartner,
    partnerCompany,
    permissions: companyPermissions,
    courierPermissions,
    partnerPermissions,
    storeScope: Array.isArray(data?.storeScope) ? data.storeScope : [],
    allStores: Boolean(data?.allStores),
    impersonation: data?.impersonation ?? null,
    companyPackage: data?.package && typeof data.package === 'object' ? data.package : null,
    modules: Array.isArray(data?.modules) ? [...data.modules] : null,
    offeringVisibility: typeof data?.offeringVisibility === 'string' ? data.offeringVisibility : null,
    activeRole: roleLabel(role, companyRole, isCourier, isCourierPartner, partnerCompany?.displayName),
    workerOnly,
  }
}

async function loadMeAndCourier(): Promise<ReturnType<typeof applyMePayload>> {
  const data = await withTimeout(fetchMe(), AUTH_BOOTSTRAP_TIMEOUT_MS, 'GET /api/me')
  const [courier, partner] = await Promise.all([fetchCourierMe(), fetchPartnerMe()])
  return applyMePayload(data, courier, partner)
}

/**
 * After Clerk login, attaches the access token to the API client and fetches
 * GET /api/me (+ optional /api/courier/me) to learn role and permission bundles.
 * The backend is the real enforcement boundary; UI gating is cosmetic alignment.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth()
  const { user } = useUser()
  const [state, setState] = useState<AuthStateCore>({
    role: 'NONE',
    companyRole: null,
    companyId: null,
    companyName: null,
    companyApprovalStatus: 'none',
    isOwner: false,
    isPlatformAdmin: false,
    isCourier: false,
    isCourierPartner: false,
    partnerCompany: null,
    permissions: EMPTY_PERMISSIONS,
    courierPermissions: EMPTY_PERMISSIONS,
    partnerPermissions: EMPTY_PERMISSIONS,
    storeScope: [],
    allStores: false,
    impersonation: null,
    companyPackage: null,
    modules: null,
    offeringVisibility: null,
    loading: true,
    accountLoadError: null,
    workerOnly: false,
    activeRole: 'None',
  })
  // Bumping this runs the account load below again.
  const [bootstrapRun, setBootstrapRun] = useState(0)
  const [previewRole, setPreviewRole] = useState<string | null>(() => {
    return getEntryContext()?.previewRole ?? null
  })

  const switchCompany = async (companyId: string) => {
    setState((s) => ({ ...s, loading: true }))
    try {
      const { data } = await api.post<MePayload>('/me/company', { companyId })
      const [courier, partner] = await Promise.all([fetchCourierMe(), fetchPartnerMe()])
      const next = applyMePayload(data, courier, partner)
      setState((s) => ({ ...s, ...next, loading: false }))
      // The stored entry context must name the new company, or the next
      // page load reads the old one back.
      const stored = getEntryContext()
      if (stored) setEntryContext({ ...stored, companyId })
      window.location.reload()
    } catch (e) {
      console.error('Failed to switch company', e)
      setState((s) => ({ ...s, loading: false }))
      throw e
    }
  }

  const refreshMe = useCallback(async () => {
    const next = await loadMeAndCourier()
    setState((s) => ({ ...s, ...next, loading: false }))
  }, [])

  const retryAccountLoad = useCallback(() => {
    setState((s) => ({ ...s, loading: true, accountLoadError: null }))
    setBootstrapRun((n) => n + 1)
  }, [])

  const startImpersonation = useCallback(async (userId: string, companyId: string) => {
    await api.post('/admin/impersonate', { userId, companyId })
    await refreshMe()
    window.location.href = '/dashboard'
  }, [refreshMe])

  const endImpersonation = useCallback(async () => {
    await api.delete('/admin/impersonate')
    await refreshMe()
    window.location.href = '/platform'
  }, [refreshMe])

  // Older sign-ins left a token and a permission list on disk. Nothing reads them
  // now, so clear what is still there.
  useEffect(() => {
    clearLegacyAuthStorage()
  }, [])

  useEffect(() => {
    if (isLoaded) {
      registerTokenResolver(async () => {
        return getToken({ template: 'api' })
      })
    }
  }, [isLoaded, getToken])

  useEffect(() => {
    const bypassToken = e2eToken()
    // False once a newer run has started: an older request can land after a newer one.
    let active = true

    if (!isLoaded) {
      return
    }

    if (!isSignedIn && !bypassToken) {
      setState((s) => (s.loading ? { ...s, loading: false } : s))
      return
    }
    ;(async () => {
      try {
        const token = bypassToken
          || (await withTimeout(
            getToken({ template: 'api' }),
            AUTH_BOOTSTRAP_TIMEOUT_MS,
            'Clerk getToken',
          ))
        if (token) setAuthToken(token)
        const next = await loadMeAndCourier()
        if (!active) return

        setState((s) => ({ ...s, ...next, loading: false, accountLoadError: null }))
      } catch (e) {
        console.error('Failed to resolve /api/me', e)
        if (active) {
          // Not "no company" (that turned merchants into applicants): report the failure.
          setState((s) => ({
            ...s,
            accountLoadError: e instanceof Error ? e.message : String(e),
            loading: false,
          }))
        }
      }
    })()
    return () => {
      active = false
    }
  }, [isLoaded, isSignedIn, getToken, bootstrapRun])

  let effectiveRole = state.role
  let effectiveIsOwner = state.isOwner
  let displayRole = previewRole || state.activeRole

  if (previewRole) {
    if (previewRole === 'Merchant' || previewRole === 'Admin') {
      effectiveRole = 'OWNER'
      effectiveIsOwner = true
    } else if (previewRole === 'Manager') {
      effectiveRole = 'MANAGER'
      effectiveIsOwner = false
    } else if (previewRole === 'Worker' || previewRole === 'Courier' || previewRole === 'Partner') {
      effectiveRole = 'WORKER'
      effectiveIsOwner = false
    } else {
      effectiveRole = 'NONE'
      effectiveIsOwner = false
    }
  }

  // Real enforcement set: company + platform admin codes already in /me, plus courier.
  const realPermissions = useMemo(() => {
    const merged = new Set(state.permissions)
    for (const p of state.courierPermissions) merged.add(p)
    for (const p of state.partnerPermissions) merged.add(p)
    return Array.from(merged)
  }, [state.permissions, state.courierPermissions, state.partnerPermissions])

  const availablePersonas = useMemo(
    () =>
      heldPersonas({
        companyRole: state.companyRole,
        role: state.role,
        isPlatformAdmin: state.isPlatformAdmin,
        isCourier: state.isCourier,
        isCourierPartner: state.isCourierPartner,
      }),
    [state.companyRole, state.role, state.isPlatformAdmin, state.isCourier, state.isCourierPartner],
  )

  const activePersona = useMemo(
    () =>
      resolveActivePersona({
        previewRole,
        held: availablePersonas,
        companyRole: state.companyRole,
        role: state.role,
        isPlatformAdmin: state.isPlatformAdmin,
        isCourier: state.isCourier,
        isCourierPartner: state.isCourierPartner,
      }),
    [
      previewRole,
      availablePersonas,
      state.companyRole,
      state.role,
      state.isPlatformAdmin,
      state.isCourier,
      state.isCourierPartner,
    ],
  )

  const hasPermission = useCallback(
    (code: string) => realPermissions.includes(code),
    [realPermissions],
  )

  const hasNavPermission = useMemo(
    () => navPermissionChecker(realPermissions, activePersona, previewRole, availablePersonas),
    [realPermissions, activePersona, previewRole, availablePersonas],
  )

  useEffect(() => {
    if (isSignedIn && user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress || '',
        username: user.fullName || '',
        role: state.role,
        companyId: state.companyId,
        companyName: state.companyName,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [isSignedIn, user, state.role, state.companyId, state.companyName])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        role: effectiveRole,
        isOwner: effectiveIsOwner,
        permissions: realPermissions,
        activeRole: displayRole,
        availablePersonas,
        activePersona,
        hasPermission,
        hasNavPermission,
        switchCompany,
        refreshMe,
        retryAccountLoad,
        startImpersonation,
        endImpersonation,
        setPreviewRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  return useContext(AuthContext)
}
